import {
  ProductCategory,
  ReportSummary,
  Producer,
  ProducerDetails,
  ReportDetails,
  ApiReport,
  SaveReportRequest,
  SaveProducerRequest,
  SaveReportResponse,
  ApiEnvelope,
  ApiProducer,
  ConfirmReportRequest,
  SendInviteRequest,
  PhoneActivationRequest,
} from "@/ReactInit/Scripts/DashboardPage/types";
import {ApiError, buildApiErrorInfo} from "./dashboardApiError";

function mapApiReportToSummary(r: ApiReport): ReportSummary {
  return {
    id: r.ReportId ?? r.reportId ?? "",
    producerId: r.ProducerId ?? r.producerId ?? "",
    year: (r.Year ?? r.year) ?? 0,
    comment: r.Comment ?? r.comment ?? null,
    reportDate: r.ReportDate ?? r.reportDate ?? null,
    changedDate: r.ChangedDate ?? r.changedDate ?? null,
    publishDate: r.PublishDate ?? r.publishDate ?? null,
    reportedBy: r.ReportedBy ?? r.reportedBy ?? null,
    published: r.Published ?? r.published ?? false,
  };
}

async function handleJsonResponse<T>(
  response: Response,
  opts?: { method?: string; fallbackMessage?: string },
): Promise<T> {
  if (!response.ok) {
    const info = await buildApiErrorInfo(response, opts);
    throw new ApiError(info);
  }
  return (await response.json()) as T;
}

async function handleVoidResponse(
  response: Response,
  opts?: { method?: string; fallbackMessage?: string },
): Promise<void> {
  if (!response.ok) {
    const info = await buildApiErrorInfo(response, opts);
    throw new ApiError(info);
  }
}

async function handleBlobResponse(
  response: Response,
  opts?: { method?: string; fallbackMessage?: string },
): Promise<Blob> {
  if (!response.ok) {
    const info = await buildApiErrorInfo(response, opts);
    throw new ApiError(info);
  }
  return await response.blob();
}

function extractReportIdFromUnknown(payload: unknown): string | null {
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;

    const direct = obj.reportId ?? obj.ReportId;
    if (typeof direct === "string" && direct.trim()) return direct;

    const data = obj.data;
    if (data && typeof data === "object") {
      const d = data as Record<string, unknown>;
      const nested = d.reportId ?? d.ReportId;
      if (typeof nested === "string" && nested.trim()) return nested;
    }
  }

  return null;
}

export async function fetchAllReports(): Promise<ReportSummary[]> {
  const res = await fetch("/api/report/getallreports", {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  const raw = await handleJsonResponse<ApiReport[]>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente rapporter.",
  });

  return raw.map(mapApiReportToSummary);
}

export async function fetchAllDraftReports(): Promise<ReportSummary[]> {
  const res = await fetch("/api/report/getalldraftreports", {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  const raw = await handleJsonResponse<ApiReport[]>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente utkast-rapporter.",
  });

  return raw.map(mapApiReportToSummary);
}

export async function fetchAllProducers(): Promise<Producer[]> {
  const res = await fetch("/api/producer/getallproducers", {
    method: "GET",
    credentials: "include",
    headers: {Accept: "application/json"},
  });

  const json = await handleJsonResponse<ApiEnvelope<ApiProducer[]>>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente produsenter.",
  });

  if (!json.success || !Array.isArray(json.data)) {
    throw new Error(json.error ?? "Uventet responsformat fra getallproducers");
  }

  return json.data.map((p) => ({
    id: p.producerId ?? "",
    orgnr: p.orgnr ?? "",
    name: p.producerName ?? "",
    address: p.address ?? null,
    phoneNumbers: (p.phoneNumbers ?? []).map((ph) => ({
      id: (ph.phoneId ?? "") || "",
      name: ph.name ?? "",
      phoneNumber: ph.phoneNumber ?? "",
      email: ph.email ?? null,
      isInvited: ph.isInvited ?? false,
      deactivated: ph.deactivated ?? false,
    })),
  }));
}

export async function fetchReportsCsv(selectedIds: string[]): Promise<Blob> {
  if (!selectedIds || selectedIds.length === 0) {
    throw new Error("Ingen rapporter valgt for eksport.");
  }

  const res = await fetch("/api/report/getcsvreports", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "text/csv, application/octet-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(selectedIds),
  });

  return await handleBlobResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke laste ned CSV.",
  });
}

export async function getReportById(reportId: string): Promise<ReportDetails> {
  const res = await fetch(`/api/report/getreportbyid?reportId=${encodeURIComponent(reportId)}`, {
    method: "GET",
    credentials: "include",
    headers: {Accept: "application/json"},
  });

  return await handleJsonResponse<ReportDetails>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente rapportdetaljer.",
  });
}

export async function getProducerById(producerId: string): Promise<ProducerDetails> {
  const res = await fetch(`/api/producer/getproducer?producerId=${encodeURIComponent(producerId)}`, {
    method: "GET",
    credentials: "include",
    headers: {Accept: "application/json"},
  });

  const json = await handleJsonResponse<ApiEnvelope<ProducerDetails>>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente produsent.",
  });

  if (!json.success || !json.data) {
    throw new Error(json.error ?? "Kunne ikke hente produsent.");
  }

  return json.data;
}

export async function fetchProductCategories(): Promise<ProductCategory[]> {
  const res = await fetch("/dashboard/json/productCategories.json", {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  return await handleJsonResponse<ProductCategory[]>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente produktkategorier.",
  });
}

export async function saveReport(body: SaveReportRequest): Promise<void> {
  const res = await fetch("/api/report/savereport", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke lagre rapport.",
  });
}

export async function deleteReport(reportId: string): Promise<void> {
  const res = await fetch("/api/report/deletereport", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json-patch+json",
    },
    body: JSON.stringify(reportId),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke slette rapport.",
  });
}

export async function saveProducer(body: SaveProducerRequest): Promise<void> {
  const res = await fetch("/api/producer/saveproducer", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke lagre virksomhet.",
  });
}

export async function publishReport(reportId: string): Promise<void> {
  const res = await fetch("/api/report/publishreport", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json-patch+json",
    },
    body: JSON.stringify(reportId),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke publisere rapport.",
  });
}

export async function saveReportWithResponse(
  body: SaveReportRequest,
): Promise<SaveReportResponse> {
  const res = await fetch("/api/report/savereport", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const info = await buildApiErrorInfo(res, {
      method: "POST",
      fallbackMessage: "Kunne ikke lagre rapport.",
    });
    throw new ApiError(info);
  }

  const json: unknown = await res.json().catch(() => null);

  const reportId = extractReportIdFromUnknown(json);
  if (reportId) return {reportId};

  throw new Error("Rapport ble lagret, men mottok ingen reportId fra backend.");
}

export async function publishReports(reportIds: string[]): Promise<void> {
  const res = await fetch("/api/report/publishreports", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json-patch+json",
    },
    body: JSON.stringify(reportIds),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke publisere rapporter.",
  });
}

export async function confirmReport(body: ConfirmReportRequest): Promise<void> {
  const res = await fetch("/api/report/confirmreport", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke bekrefte rapport.",
  });
}

export async function sendInvite(body: SendInviteRequest): Promise<void> {
  const res = await fetch("/api/producer/sendinvite", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke sende invitasjon.",
  });
}

export async function setPhoneActivation(body: PhoneActivationRequest): Promise<void> {
  const res = await fetch("/api/producer/setphoneactivation", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  await handleVoidResponse(res, {
    method: "POST",
    fallbackMessage: "Kunne ikke oppdatere aktivering.",
  });
}

export async function fetchReportsByProducer(producerId: string): Promise<ReportSummary[]> {
  const res = await fetch(
    `/api/report/getreportsbyproducer?producerId=${encodeURIComponent(producerId)}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    },
  );

  const json = await handleJsonResponse<ApiEnvelope<ApiReport[]>>(res, {
    method: "GET",
    fallbackMessage: "Kunne ikke hente rapporter for virksomheten.",
  });

  if (!json.success || !Array.isArray(json.data)) {
    throw new Error(json.error ?? "Uventet responsformat fra getreportsbyproducer");
  }

  return json.data.map(mapApiReportToSummary);
}
